import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import FilePreview from "../../FilePreview/FilePreview";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getApprovalVariant,
} from "../../common/adminTable";
import {
  getSuppliers,
  searchSuppliers,
  getSupplierById,
  updateSupplier,
  deleteSupplier,
} from "./service";
import useTableSearch from "../../../hooks/useTableSearch";

const SERVICE_OPTIONS = [
  { value: "package", label: "Package", icon: "fi fi-rr-backpack" },
  { value: "activity", label: "Activity", icon: "fi fi-rr-hiking" },
  { value: "attraction", label: "Attraction", icon: "fi fi-rr-landmark" },
  { value: "event", label: "Event", icon: "fi fi-rr-calendar-star" },
  { value: "rental", label: "Rental", icon: "fi fi-rr-car" },
];

const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";
const inputClass =
  "block w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors";

const STAGE_OPTIONS = [
  {
    value: "0",
    label: "Pending",
    icon: "fi fi-rr-hourglass",
    activeClass: "bg-amber-100 text-amber-800 shadow-sm",
  },
  {
    value: "1",
    label: "Approved",
    icon: "fi fi-rr-check-circle",
    activeClass: "bg-green-100 text-green-800 shadow-sm",
  },
  {
    value: "2",
    label: "Rejected",
    icon: "fi fi-rr-cross-circle",
    activeClass: "bg-red-100 text-red-800 shadow-sm",
  },
];

const STATUS_OPTIONS = [
  {
    value: "1",
    label: "Active",
    icon: "fi fi-rr-power",
    activeClass: "bg-primary text-white shadow-sm",
  },
  {
    value: "0",
    label: "Inactive",
    icon: "fi fi-rr-ban",
    activeClass: "bg-gray-200 text-gray-800 shadow-sm",
  },
];

const EXPLOREWORLD_OPTIONS = [
  {
    value: "1",
    label: "Yes",
    icon: "fi fi-rr-badge-check",
    activeClass: "bg-teal-600 text-white shadow-sm",
  },
  {
    value: "0",
    label: "No",
    icon: "fi fi-rr-minus-circle",
    activeClass: "bg-gray-200 text-gray-800 shadow-sm",
  },
];

const CompactSegment = ({ label, required, value, options, onChange }) => (
  <div>
    <label className="mb-1.5 block text-xs font-medium text-gray-600">
      {label} {required ? <span className="text-red-500">*</span> : null}
    </label>
    <div
      className="flex rounded-lg border border-gray-200 bg-gray-50 p-1"
      role="radiogroup"
      aria-label={label}
    >
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(option.value)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-all ${
              isSelected
                ? option.activeClass
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            <i className={`${option.icon} text-[11px]`} />
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

const FormSection = ({ title, icon, children }) => (
  <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <i className={`${icon} text-sm leading-none`} />
      </span>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
    {children}
  </section>
);

const parseServiceProvided = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // fall through to comma-separated parsing
    }

    return value
      .replace(/[\[\]"]/g, "")
      .split(",")
      .map((service) => service.trim())
      .filter(Boolean);
  }

  return [];
};

const Suppliers = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    company_phone: "",
    company_website: "",
    company_registration_number: "",
    gst_number: "",
    service_provided: [],
    profile_image: null,
    cin_registration_form: null,
    status: "",
    is_approved: "",
    is_exploreworld: "0",
  });

  // React Query hooks
  const { data: suppliersData } = useQuery({
    queryKey: ["suppliers", currentPage, activeSearch],
    queryFn: () =>
      activeSearch
        ? searchSuppliers({ search: activeSearch, page: currentPage })
        : getSuppliers(currentPage),
    keepPreviousData: true,
  });

  const suppliers = Array.isArray(suppliersData?.data)
    ? suppliersData.data
    : [];
  const pagination = {
    currentPage: suppliersData?.current_page || 1,
    lastPage: suppliersData?.last_page || 1,
    perPage: suppliersData?.per_page || 10,
    total: suppliersData?.total || 0,
    from: suppliersData?.from || 0,
    to: suppliersData?.to || 0,
  };

  // Mutations
  const updateMutation = useMutation({
    mutationFn: updateSupplier,
    onSuccess: () => {
      queryClient.invalidateQueries(["suppliers"]);
      setEditDialogOpen(false);
      showSuccessMessage("Supplier updated successfully!");
    },
    onError: (error) => {
      showErrorMessage(
        error.response?.data?.message || "Failed to update supplier"
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSupplier,
    onSuccess: () => {
      queryClient.invalidateQueries(["suppliers"]);
      setDeleteDialogOpen(false);
      showSuccessMessage("Supplier deleted successfully!");
    },
    onError: (error) => {
      showErrorMessage(
        error.response?.data?.message || "Failed to delete supplier"
      );
    },
  });

  // Helper functions
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setErrorMessage(errorMsg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Event handlers
  const handleEditClick = async (supplierId) => {
    try {
      const response = await getSupplierById(supplierId);
      const supplierData = response.data;
      setSelectedSupplier(supplierData);
      setFormData({
        name: supplierData.user.name,
        email: supplierData.user.email,
        phone: supplierData.user.phone,
        company_name: supplierData.company_name,
        company_phone: supplierData.company_phone,
        company_website: supplierData.company_website,
        company_registration_number: supplierData.company_registration_number,
        gst_number: supplierData.gst_number || "",
        service_provided: parseServiceProvided(supplierData.service_provided),
        status: supplierData.status.toString(),
        is_approved: supplierData.is_approved.toString(),
        is_exploreworld: supplierData.is_exploreworld ? "1" : "0",
        profile_image: null,
        cin_registration_form: null,
        profile_image_url: supplierData.profile_image_url,
        cin_registration_form_url: supplierData.cin_registration_form_url,
      });
      setEditDialogOpen(true);
    } catch (error) {
      showErrorMessage(
        error.response?.data?.message || "Failed to fetch supplier details"
      );
    }
  };

  const handleDeleteClick = (supplier) => {
    setSelectedSupplier(supplier);
    setDeleteDialogOpen(true);
  };

  const handleUpdateSupplier = () => {
    if (selectedSupplier) {
      const formDataToSend = new FormData();

      // Append form data
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("company_name", formData.company_name);
      formDataToSend.append("company_phone", formData.company_phone);
      formDataToSend.append("company_website", formData.company_website);
      formDataToSend.append(
        "company_registration_number",
        formData.company_registration_number
      );
      formDataToSend.append("gst_number", formData.gst_number || "");
      formDataToSend.append(
        "service_provided",
        JSON.stringify(formData.service_provided || [])
      );
      formDataToSend.append("status", formData.status);
      formDataToSend.append("is_approved", formData.is_approved);
      formDataToSend.append("is_exploreworld", formData.is_exploreworld || "0");

      if (formData.profile_image instanceof File) {
        formDataToSend.append("profile_image", formData.profile_image);
      }
      if (formData.cin_registration_form instanceof File) {
        formDataToSend.append(
          "cin_registration_form",
          formData.cin_registration_form
        );
      }

      updateMutation.mutate({
        id: selectedSupplier.id,
        supplierData: formDataToSend,
      });
    }
  };

  const handleDeleteSupplier = () => {
    if (selectedSupplier) {
      deleteMutation.mutate(selectedSupplier.id);
    }
  };

  const toggleService = (service) => {
    setFormData((prev) => {
      const current = prev.service_provided || [];
      const next = current.includes(service)
        ? current.filter((item) => item !== service)
        : [...current, service];
      return { ...prev, service_provided: next };
    });
  };

  const hasAttachments =
    formData.profile_image_url ||
    formData.profile_image ||
    formData.cin_registration_form_url ||
    formData.cin_registration_form;

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  const pageMeta = ADMIN_PAGE_META.suppliers;

  return (
    <AdminPageLayout>
      {showError && errorMessage && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => {
            setShowError(false);
            setErrorMessage("");
          }}
        />
      )}

      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, company..."
            />
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Company Name</AdminTableTh>
            <AdminTableTh>Contact Person</AdminTableTh>
            <AdminTableTh>Email</AdminTableTh>
            <AdminTableTh>Phone</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh>ExploreWorld</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {suppliers.length > 0 ? (
              suppliers.map((supplier, index) => (
                <AdminTableRow key={supplier.id}>
                  <AdminTableCell>{index + 1}</AdminTableCell>
                  <AdminTableCell>{supplier.company_name}</AdminTableCell>
                  <AdminTableCell>{supplier.user.name}</AdminTableCell>
                  <AdminTableCell>{supplier.user.email}</AdminTableCell>
                  <AdminTableCell>{supplier.user.phone}</AdminTableCell>
                  <AdminTableCell>
                    <StatusBadge variant={getApprovalVariant(supplier.is_approved)}>
                      {supplier.is_approved === 0
                        ? "Pending"
                        : supplier.is_approved === 1
                        ? "Approved"
                        : "Rejected"}
                    </StatusBadge>
                  </AdminTableCell>
                  <AdminTableCell>
                    <StatusBadge
                      variant={supplier.is_exploreworld ? "approved" : "inactive"}
                    >
                      {supplier.is_exploreworld ? "Yes" : "No"}
                    </StatusBadge>
                  </AdminTableCell>
                  <TableActionsCell>
                    <EditActionButton
                      showLabel={false}
                      onClick={() => handleEditClick(supplier.id)}
                    />
                    <DeleteActionButton
                      showLabel={false}
                      onClick={() => handleDeleteClick(supplier)}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            ) : (
              <TableEmptyRow
                colSpan={8}
                searchQuery={activeSearch}
                emptyMessage="No suppliers found"
                searchMessage="No suppliers found matching your search"
                onClearSearch={clearSearch}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        variant="detail"
        width="w-full"
        maxWidth="4xl"
        title="Edit Supplier"
        primaryButtonText="Save Changes"
        primaryButtonAction={handleUpdateSupplier}
        closeButtonText="Cancel"
        closeButtonAction={() => setEditDialogOpen(false)}
        isLoading={updateMutation.isPending}
      >
        <div className="space-y-5">
          <FormSection title="Permissions" icon="fi fi-rr-shield-check">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <CompactSegment
                label="Approval Stage"
                required
                value={formData.is_approved || "0"}
                options={STAGE_OPTIONS}
                onChange={(value) =>
                  setFormData({ ...formData, is_approved: value })
                }
              />
              <CompactSegment
                label="Account Status"
                required
                value={formData.status || "1"}
                options={STATUS_OPTIONS}
                onChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              />
              <CompactSegment
                label="ExploreWorld Supplier"
                required
                value={formData.is_exploreworld || "0"}
                options={EXPLOREWORLD_OPTIONS}
                onChange={(value) =>
                  setFormData({ ...formData, is_exploreworld: value })
                }
              />
            </div>
          </FormSection>

          <FormSection title="Personal Information" icon="fi fi-rr-user">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className={labelClass}>
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </div>

              <div>
                <label className={labelClass}>
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className={inputClass}
                  placeholder="Enter email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>

              <div className="sm:col-span-2 lg:col-span-1">
                <label className={labelClass}>
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  className={inputClass}
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Company Information" icon="fi fi-rr-building">
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelClass}>
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="Enter company name"
                    value={formData.company_name}
                    onChange={(e) =>
                      setFormData({ ...formData, company_name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Company Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    className={inputClass}
                    placeholder="Enter company phone"
                    value={formData.company_phone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        company_phone: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="sm:col-span-2 lg:col-span-1">
                  <label className={labelClass}>
                    Company Website <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    className={inputClass}
                    placeholder="https://example.com"
                    value={formData.company_website}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        company_website: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>
                    Company Registration Number{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="Enter registration number"
                    value={formData.company_registration_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        company_registration_number: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className={labelClass}>GST Number (GSTIN)</label>
                  <input
                    type="text"
                    className={`${inputClass} uppercase`}
                    placeholder="e.g. 22AAAAA0000A1Z5"
                    maxLength={15}
                    value={formData.gst_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        gst_number: e.target.value.toUpperCase(),
                      })
                    }
                  />
                  <p className="mt-1.5 text-xs text-gray-500">
                    Set the supplier GST number here. Once saved, the supplier
                    can update it from their profile.
                  </p>
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  Services Provided <span className="text-red-500">*</span>
                </label>
                <p className="mb-3 text-xs text-gray-500">
                  Select all services this supplier offers on the platform.
                </p>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((option) => {
                    const isSelected = (formData.service_provided || []).includes(
                      option.value
                    );
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleService(option.value)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-white"
                        }`}
                      >
                        <i className={`${option.icon} text-xs`} />
                        {option.label}
                        {isSelected ? (
                          <i className="fi fi-rr-check text-xs" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </FormSection>

          <FormSection title="Documents" icon="fi fi-rr-folder-upload">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelClass}>Profile Image</label>
                <label className="group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 px-4 py-6 transition-colors hover:border-primary/40 hover:bg-primary/5">
                  <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm group-hover:text-primary">
                    <i className="fi fi-rr-picture text-lg" />
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    {formData.profile_image
                      ? formData.profile_image.name
                      : "Upload profile image"}
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    PNG, JPG up to 5MB
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        profile_image: e.target.files[0],
                      })
                    }
                    className="sr-only"
                  />
                </label>
              </div>

              <div>
                <label className={labelClass}>CIN Registration Form</label>
                <label className="group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 px-4 py-6 transition-colors hover:border-primary/40 hover:bg-primary/5">
                  <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm group-hover:text-primary">
                    <i className="fi fi-rr-document text-lg" />
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    {formData.cin_registration_form
                      ? formData.cin_registration_form.name
                      : "Upload CIN document"}
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    PDF, DOC, or DOCX
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cin_registration_form: e.target.files[0],
                      })
                    }
                    className="sr-only"
                  />
                </label>
              </div>
            </div>

            {hasAttachments ? (
              <div className="mt-5 border-t border-gray-100 pt-5">
                <h4 className="mb-3 text-sm font-medium text-gray-700">
                  Current Attachments
                </h4>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {formData.profile_image_url && !formData.profile_image ? (
                    <FilePreview
                      fileUrl={formData.profile_image_url}
                      label="Profile Image"
                      onDelete={() =>
                        setFormData({ ...formData, profile_image_url: null })
                      }
                      isImage={true}
                    />
                  ) : null}
                  {formData.profile_image ? (
                    <FilePreview
                      file={formData.profile_image}
                      label="Profile Image"
                      onDelete={() =>
                        setFormData({ ...formData, profile_image: null })
                      }
                      isImage={true}
                    />
                  ) : null}
                  {formData.cin_registration_form_url &&
                  !formData.cin_registration_form ? (
                    <FilePreview
                      fileUrl={formData.cin_registration_form_url}
                      label="CIN Registration Form"
                      onDelete={() =>
                        setFormData({
                          ...formData,
                          cin_registration_form_url: null,
                        })
                      }
                      isImage={false}
                    />
                  ) : null}
                  {formData.cin_registration_form ? (
                    <FilePreview
                      file={formData.cin_registration_form}
                      label="CIN Registration Form"
                      onDelete={() =>
                        setFormData({ ...formData, cin_registration_form: null })
                      }
                      isImage={false}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </FormSection>
        </div>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-base text-red-600"></i>
            Delete Supplier
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteSupplier}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this supplier? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default Suppliers;
